import { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../components/Card";
import axios from "../utils/axiosConfig";
import { Artifact } from "../types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { useWebSocket } from "../providers/WebSocketProvider";
import { ProgressStatus } from "../components/ProgressStatus";
import { confirm } from '../providers/ConfirmationProvider';
import { toast } from "hooks/use-toast";
import { isTaskBusy, mergeProgress } from "../utils/taskStatus";

export function ArtifactsPage() {
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [tagFilter, setTagFilter] = useState<string>("");

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const navigate = useNavigate();
  const { eventBus } = useWebSocket();

  // Live progress from WebSocket - never overwritten by fetch, avoids race with setInterval
  const liveProgressRef = useRef<Record<number, { translation?: number; speech?: number; transcript?: number }>>({});

  // Format date (only date without time)
  const formatDate = (dateString?: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  // Fetch artifacts function
  const fetchArtifacts = async () => {
    try {
      const { data } = await axios.get<Artifact[]>("/v2/translations");
      const live = liveProgressRef.current;

      setArtifacts(prev =>
        data.map((fetched) => {
          const fid = Number(fetched.id);
          const existing = prev.find((a) => Number(a.id) === fid);
          const liveForId = live[fid];

          const translationProgress = mergeProgress(
            existing?.translation_progress,
            fetched.translation_progress,
            liveForId?.translation,
          );
          const speechProgress = mergeProgress(
            existing?.speech_generation_progress,
            fetched.speech_generation_progress,
            liveForId?.speech,
          );
          const transcriptProgress = mergeProgress(
            existing?.text_generation_progress,
            fetched.text_generation_progress,
            liveForId?.transcript,
          );
          if (liveForId) {
            if (translationProgress === 100) delete liveForId.translation;
            if (speechProgress === 100) delete liveForId.speech;
            if (transcriptProgress === 100) delete liveForId.transcript;
            if (Object.keys(liveForId).length === 0) delete live[fid];
          }
          return {
            ...fetched,
            translation_progress: translationProgress,
            speech_generation_progress: speechProgress,
            text_generation_progress: transcriptProgress,
          };
        }),
      );
    } catch (error) {
      console.error("Error fetching artifacts:", error);
    }
  };

  useEffect(() => {
    fetchArtifacts();
    const intervalId = setInterval(fetchArtifacts, 30000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const handleTr = (data: { translation_id: number; progress: number }) => {
      const tid = Number(data.translation_id);
      if (!(tid in liveProgressRef.current)) liveProgressRef.current[tid] = {};
      liveProgressRef.current[tid].translation = data.progress;
      setArtifacts((prev) => {
        const hasArtifact = prev.some((a) => Number(a.id) === tid);
        if (!hasArtifact) {
          fetchArtifacts();
          return prev;
        }
        return prev.map((a) =>
          Number(a.id) === tid
            ? { ...a, translation_progress: mergeProgress(a.translation_progress, data.progress) }
            : a
        );
      });
    };
    const handleVo = (data: { translation_id: number; progress: number }) => {
      const tid = Number(data.translation_id);
      if (!(tid in liveProgressRef.current)) liveProgressRef.current[tid] = {};
      liveProgressRef.current[tid].speech = data.progress;
      setArtifacts((prev) => {
        const hasArtifact = prev.some((a) => Number(a.id) === tid);
        if (!hasArtifact) {
          fetchArtifacts();
          return prev;
        }
        return prev.map((a) =>
          Number(a.id) === tid
            ? { ...a, speech_generation_progress: mergeProgress(a.speech_generation_progress, data.progress) }
            : a
        );
      });
    };
    const handleTs = (data: { translation_id: number; progress: number }) => {
      const tid = Number(data.translation_id);
      if (!(tid in liveProgressRef.current)) liveProgressRef.current[tid] = {};
      liveProgressRef.current[tid].transcript = data.progress;
      setArtifacts((prev) => {
        const hasArtifact = prev.some((a) => Number(a.id) === tid);
        if (!hasArtifact) {
          fetchArtifacts();
          return prev;
        }
        return prev.map((a) =>
          Number(a.id) === tid
            ? { ...a, text_generation_progress: mergeProgress(a.text_generation_progress, data.progress) }
            : a
        );
      });
    };

    eventBus.on("tr_progress", handleTr);
    eventBus.on("vo_progress", handleVo);
    eventBus.on("ts_progress", handleTs);
    return () => {
      eventBus.off("tr_progress", handleTr);
      eventBus.off("vo_progress", handleVo);
      eventBus.off("ts_progress", handleTs);
    };
  }, [eventBus]);

  const handleRowClick = (artifact: Artifact) => {
    const live = liveProgressRef.current[Number(artifact.id)];
    if (isTaskBusy(artifact, live, artifact.job_status ?? null)) {
      toast({
        title: "Task in progress",
        description: "Open this item when processing has finished.",
      });
      return;
    }
    if (artifact.job_status === "prepared_to_voiceover" && artifact.job_type === "step_by_step_voiceover") {
      navigate(`/voiceover/step-by-step/${artifact.id}`);
    } else {
      navigate(`/translation/${artifact.id}`);
    }
  };

  const handleDelete = async (id: number) => {
    confirm(
      "Are you sure you want to delete this artifact?",
      async () => {
        try {
          await axios.delete(`/v2/translation/${id}`);
          setArtifacts((artifacts) =>
            artifacts.filter((artifact) => artifact.id !== id)
          );
          toast({ title: "Success", description: "Artifact deleted successfully!" });
        } catch (error) {
          console.error("Error deleting artifact:", error);
        }
      },
      () => {
        toast({ title: "Info", description: "Artifact deletion cancelled." });
      }
    );
  };

  const allTags = useMemo(() => {
    const setTags = new Set<string>();
    artifacts.forEach((a) => setTags.add(a.tag ?? "Untagged"));
    return Array.from(setTags).sort();
  }, [artifacts]);

  const filteredData = artifacts.filter((artifact) => {
    const matchesText =
      artifact.name?.toLowerCase().includes(globalFilter.toLowerCase()) ?? false;
    const tagValue = artifact.tag ?? "Untagged";
    const matchesTag = tagFilter === "" || tagValue === tagFilter;
    return matchesText && matchesTag;
  });

  const pageCount = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    pageIndex * itemsPerPage,
    (pageIndex + 1) * itemsPerPage
  );

  return (
    <div className="min-h-screen px-4 py-12 sm:px-6 lg:px-8 bg-gray-50">
      <div className="max-w-full mx-auto space-y-6">
        {/* Title */}
        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-gray-700">Tasks</h1>
        </div>

        <Card>
          {/* Filters */}
          <div className="flex flex-col gap-4 mb-6 md:flex-row md:justify-between items-center">
            <input
              type="text"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                setPageIndex(0);
              }}
              placeholder="Search artifacts..."
              className="block w-full md:w-1/3 px-4 py-2 text-sm border rounded shadow focus:ring-blue-500 focus:border-blue-500"
            />

            <select
              value={tagFilter}
              onChange={(e) => {
                setTagFilter(e.target.value);
                setPageIndex(0);
              }}
              className="px-4 py-2 text-sm border rounded shadow focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Tags</option>
              {allTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>

            <select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setPageIndex(0);
              }}
              className="px-4 py-2 text-sm border rounded shadow focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {/* Table */}
          {filteredData.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="overflow-hidden border border-gray-200 shadow sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200 table-auto">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Name
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Tag
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Language
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Date
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Progress Status
                        </th>
                        <th className="px-6 py-3 text-xs font-medium text-left text-gray-500 uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedData.map((artifact) => {
                        const rowBusy = isTaskBusy(
                          artifact,
                          liveProgressRef.current[Number(artifact.id)],
                          artifact.job_status ?? null
                        );
                        return (
                        <tr
                          key={artifact.id}
                          className={
                            rowBusy
                              ? "cursor-not-allowed opacity-70 hover:bg-gray-50"
                              : "cursor-pointer hover:bg-gray-100"
                          }
                          onClick={() => handleRowClick(artifact)}
                        >
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {artifact.name ?? ""}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {artifact.tag ?? "Untagged"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {artifact.target_language}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {formatDate(artifact.created_at)}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <ProgressStatus
                              transcriptProgress={mergeProgress(
                                artifact.text_generation_progress,
                                liveProgressRef.current[Number(artifact.id)]?.transcript,
                              )}
                              translationProgress={mergeProgress(
                                artifact.translation_progress,
                                liveProgressRef.current[Number(artifact.id)]?.translation,
                              )}
                              speechProgress={mergeProgress(
                                artifact.speech_generation_progress,
                                liveProgressRef.current[Number(artifact.id)]?.speech,
                              )}
                              paymentLink={artifact.payment_link}
                              jobStatus={artifact.job_status}
                            />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(artifact.id);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center py-20">
              <div className="text-center">
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">
                  No Artifacts Found
                </h2>
                <p className="text-gray-500 mb-6">
                  You haven't created any translation tasks yet. Click{" "}
                  <Link
                    to="/upload"
                    className="text-blue-500 underline hover:text-blue-700"
                  >
                    here
                  </Link>{" "}
                  to upload a file and get started!
                </p>
                <a
                  href="/upload"
                  className="inline-block px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Upload File
                </a>
              </div>
            </div>
          )}

          {/* Pagination */}
          {filteredData.length > 0 && (
            <div className="flex justify-between mt-4">
              <button
                onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
                disabled={pageIndex === 0}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {pageIndex + 1} of {pageCount}
              </span>
              <button
                onClick={() =>
                  setPageIndex((i) => Math.min(pageCount - 1, i + 1))
                }
                disabled={pageIndex === pageCount - 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

